import React, { FC } from 'react'
import classNames from 'classnames'
import { Text } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { useTheaterPreferences } from '../TheaterPreferencesContext'
import { getDaysOfWeek, getTimeWindows } from '../constants'

interface DayTimePreferencesListProps {
  dayTimePreferences: Record<string, string[]>
}

const TimeWindowButton: FC<{
  isSelected: boolean
  label: string
  dayId: string
  windowId: string
}> = ({ isSelected, label, dayId, windowId }) => {
  const { handleDayTimeToggle } = useTheaterPreferences()

  return (
    <button
      onClick={() => handleDayTimeToggle(dayId, windowId)}
      className={classNames(
        'rounded-full px-4 py-1.5 text-xs font-normal transition-colors',
        isSelected ? 'bg-black text-white border border-black' : 'bg-core-gray-100 text-black border border-black',
      )}
    >
      {label}
    </button>
  )
}

export const DayTimePreferencesList: FC<DayTimePreferencesListProps> = ({ dayTimePreferences }) => {
  const { t } = useTranslate('account')

  // Get translated constants
  const daysOfWeek = getDaysOfWeek(t)
  const timeWindows = getTimeWindows(t)

  // Get all days that have an entry in dayTimePreferences (selected days)
  const selectedDayIds = Object.keys(dayTimePreferences)

  if (selectedDayIds.length === 0) {
    return (
      <Text className="text-xs text-core-gray-500">
        {t('noDaysAndTimesSelected', 'No preferred days and times selected')}
      </Text>
    )
  }

  const sortedDayIds = selectedDayIds.toSorted((a, b) => {
    const aIndex = daysOfWeek.findIndex((d) => d.id === a)
    const bIndex = daysOfWeek.findIndex((d) => d.id === b)
    return aIndex - bIndex
  })

  return (
    <div className="flex flex-col gap-4">
      {sortedDayIds.map((dayId) => {
        const selectedTimeWindows = dayTimePreferences[dayId] || []
        const dayLabel = daysOfWeek.find((d) => d.id === dayId)?.label || ''

        return (
          <div key={dayId} className="flex flex-col gap-2 rounded-xl bg-core-gray-100 p-4">
            <Text weight="semibold" className="text-sm">
              {dayLabel}
            </Text>
            <div className="flex flex-wrap gap-2">
              {timeWindows.map((window) => (
                <TimeWindowButton
                  key={window.id}
                  isSelected={selectedTimeWindows.includes(window.id)}
                  label={window.label}
                  dayId={dayId}
                  windowId={window.id}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
